import React, { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import LoanLayout from "./LoanLayout";
import Layout from "./Layout";
import ScrollToTop from "./components/ScrollTop";
import ProtectedRoute from "./components/ProtectedRoute";
import { EKYC_STEP_PATH, AA_STEP_PATH, THANK_YOU_PATH } from "./pages/Dashboard/ThankYou/journeyPaths";
import PixelTracker from "./components/PixelTracker";
import Home from "./pages/Home";
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const Contact = lazy(() => import("./pages/Contact"));
const ApplyForLoan = lazy(() => import("./pages/ApplyForLoan"));
const ShortLoan = lazy(() => import("./pages/ShortLoan"));
const InstantLoan = lazy(() => import("./pages/InstantLoan"));
const FAQ = lazy(() => import("./pages/FAQ"));
const RateandTerms = lazy(() => import("./pages/RateandTerms"));
const FairPracticeCode = lazy(() => import("./pages/FairPracticeCode"));
const Grievance = lazy(() => import("./pages/Grievance"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsandConditions = lazy(() => import("./pages/TermsandConditions"));
const RepayLoan = lazy(() => import("./pages/RepayLoan"));
const Flexibility = lazy(() => import("./pages/Flexibility"));
const DebtConsolidation = lazy(() => import("./pages/DebtConsolidation"));
const MinimalCommitment = lazy(() => import("./pages/MinimalCommitment"));
const CostEffective = lazy(() => import("./pages/CostEffective"));
const RepayThankyou = lazy(() => import("./pages/RepayThanku"));
const DeletionPolicy = lazy(() => import("./pages/DeletionPolicy"));
const RegionalHubsMap = lazy(() => import("./pages/RegionalHubsMap"));
const ReturnCancellationPolicy = lazy(() => import("./pages/ReturnCancellationPolicy"));

// City Locations (lazy loaded)
const Delhi = lazy(() => import("./pages/locations/Delhi"));
const Banglore = lazy(() => import("./pages/locations/Banglore"));
const Mumbai = lazy(() => import("./pages/locations/Mumbai"));
const Ahmedabad = lazy(() => import("./pages/locations/Ahmedabad"));
const Hyderabad = lazy(() => import("./pages/locations/Hyderabad"));
const Gurgaon = lazy(() => import("./pages/locations/Gurgaon"));
const Noida = lazy(() => import("./pages/locations/Noida"));
const Jaipur = lazy(() => import("./pages/locations/Jaipur"));
const GreaterNoida = lazy(() => import("./pages/locations/GreaterNoida"));
const Ghaziabad = lazy(() => import("./pages/locations/Ghaziabad"));
const Kolkata = lazy(() => import("./pages/locations/Kolkata"));
const Chennai = lazy(() => import("./pages/locations/Chennai"));
const Coimbatore = lazy(() => import("./pages/locations/Coimbatore"));
const Visakhapatnam = lazy(() => import("./pages/locations/Visakhapatnam"));

// Needs (lazy loaded)
const Travel = lazy(() => import("./pages/needs/Travel"));
const Medical = lazy(() => import("./pages/needs/Medical"));
const Teachers = lazy(() => import("./pages/needs/Teachers"));
const TwoWheeler = lazy(() => import("./pages/needs/TwoWheeler"));
const Shopping = lazy(() => import("./pages/needs/Shopping"));
const Engineer = lazy(() => import("./pages/needs/Engineer"));

// Dashboard Pages (lazy loaded)
const DashBoard = lazy(() => import("./pages/Dashboard/DashBoard"));
const PanDetails = lazy(() => import("./pages/Dashboard/PanDetails/PanDetails"));
const CapturAddress = lazy(() => import("./pages/Dashboard/CapturAddress/CapturAddress"));
const CapturPersonalInformation = lazy(() => import("./pages/Dashboard/CapturPersonalInformation/CapturPersonalInformation"));
const CombinedApplicationFlow = lazy(() => import("./pages/Dashboard/CombinedApplicationFlow/CombinedApplicationFlow"));
const CapturIncomeDetails = lazy(() => import("./pages/Dashboard/CapturIncomeDetails/CapturIncomeDetails"));
const UploadPicture = lazy(() => import("./pages/Dashboard/UploadPicture/UploadPicture"));
const ProfilePage = lazy(() => import("./pages/Dashboard/ProfilePage/ProfilePage"));
const ProfilePreview = lazy(() => import("./pages/Dashboard/ProfilePage/ProfilePreview"));
const CalculateLoan = lazy(() => import("./pages/Dashboard/CalculateLoan/CalculateLoan"));
const AboutCompany = lazy(() => import("./pages/Dashboard/AboutCompany/AboutCompany"));
const BankDetail = lazy(() => import("./pages/Dashboard/BankDetail/BankDetail"));
const Ekyc = lazy(() => import("./pages/Dashboard/Ekyc/Ekyc"));
const UploadBankStatement = lazy(() => import("./pages/Dashboard/UploadBankStatement/UploadBankStatement"));
const PanUpload = lazy(() => import("./pages/Dashboard/PanUpload/PanUpload"));
const DocumentUpload = lazy(() => import("./pages/Dashboard/DocumentUpload/DocumentUpload"));
const ThankYou = lazy(() => import("./pages/Dashboard/ThankYou/ThankYou"));
const AccountAggregatorStep = lazy(() => import("./pages/Dashboard/ThankYou/AccountAggregatorStep"));
const AadhaarEkycStep = lazy(() => import("./pages/Dashboard/ThankYou/AadhaarEkycStep"));
const UploadSalaryslip = lazy(() => import("./pages/Dashboard/UploadSalaryslip/UploadSalaryslip"));
const UploadUtilitybill = lazy(() => import("./pages/Dashboard/UploadUtilitybill/UploadUtilitybill"));
const LeadPreview = lazy(() => import("./pages/Dashboard/ProfilePage/LeadPreview"));
const LoanHistory = lazy(() => import("./pages/LoanHistory/LoanHistory"));
const LoanDetail = lazy(() => import("./pages/LoanHistory/LoanDetail"));
const RepayPage = lazy(() => import("./pages/Dashboard/ProfilePage/RepayPage"));
const Eligibility = lazy(() => import("./pages/Dashboard/Eligibility/Eligibility"));
const showmessage = async message => {
  try {
    var x = document.getElementById("snackbar");
    x.className = "show";
    x.innerText = message;
    setTimeout(function () {
      x.className = x.className.replace("show", "");
    }, 3000);
  } catch (error) { }
};
// Suspense wraps the entire Routes — correct pattern for React Router v6

// Old eKYC step URL: keep links already sent (and legacy ?aa=return / ?ekyc=return returns) working.
function LegacyEkycRedirect() {
  const { search } = useLocation();
  return <Navigate to={EKYC_STEP_PATH + search} replace />;
}

function Router() {
  return (
    <>
      <ScrollToTop />
      <PixelTracker />
      <Suspense fallback={null}>
        <Routes>
          {/* ─── Public Layout ─── */}
          <Route path="/" element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="home" element={<Home />} />
            <Route path="about-ActoLoan" element={<About />} />
            <Route path="ActoLoan-Services" element={<Services />} />
            <Route path="contact-customer-care-ActoLoan" element={<Contact />} />
            <Route path="borrower-loan" element={<ShortLoan />} />
            <Route path="low-cibil" element={<InstantLoan />} />
            <Route path="faq" element={<FAQ />} />
            <Route path="rateandterms" element={<RateandTerms />} />
            <Route path="fairpracticecode" element={<FairPracticeCode />} />
            <Route path="grievances" element={<Grievance />} />
            <Route path="privacypolicy" element={<PrivacyPolicy />} />
            <Route path="termsandconditions" element={<TermsandConditions />} />
            <Route path="deletion-policy" element={<DeletionPolicy />} />
            <Route path="return-and-cancellation-policy" element={<ReturnCancellationPolicy />} />
            <Route path="regional-hubs" element={<RegionalHubsMap />} />
            <Route path="mini-loan" element={<Flexibility />} />
            <Route path="instant-loan" element={<DebtConsolidation />} />
            <Route path="minimalcommitment" element={<MinimalCommitment />} />
            <Route path="costeffective" element={<CostEffective />} />
            <Route path="thanku" element={<RepayThankyou />} />
            <Route path="apply-now" element={<ApplyForLoan showmessage={showmessage} />} />
            <Route path="repayloan" element={<RepayLoan showmessage={showmessage} />} />
            {/* City Pages */}
            <Route path="Apply-for-personal-loan-in-New-Delhi" element={<Delhi />} />
            <Route path="Apply-for-instant-cash-loan-in-Bangalore" element={<Banglore />} />
            <Route path="Apply-for-instant-personal-loan-in-Mumbai" element={<Mumbai />} />
            <Route path="Apply-for-personal-loan-in-ahmedabad" element={<Ahmedabad />} />
            <Route path="instant-personal-loan-online-in-hyderabad" element={<Hyderabad />} />
            <Route path="get-instant-personal-loan-in-Gurgaon" element={<Gurgaon />} />
            <Route path="Apply-for-personal-loan-in-noida" element={<Noida />} />
            <Route path="Apply-for-easy-personal-loan-in-jaipur" element={<Jaipur />} />
            <Route path="instant-personal-loan-in-greater-noida" element={<GreaterNoida />} />
            <Route path="Apply-for-instant-personal-loan-in-ghaziabad" element={<Ghaziabad />} />
            <Route path="Apply-for-instant-personal-loan-in-Kolkata" element={<Kolkata />} />
            <Route path="Apply-for-instant-personal-loan-in-chennai" element={<Chennai />} />
            <Route path="Apply-for-instant-personal-loan-in-coimbatore" element={<Coimbatore />} />
            <Route path="Apply-for-personal-loan-online-in-visakhapatnam" element={<Visakhapatnam />} />
            {/* Need-Based Pages */}
            <Route path="Instant-personal-loan-for-travel" element={<Travel />} />
            <Route path="Instant-personal-loan-for-medical-emergency" element={<Medical />} />
            <Route path="Instant-personal-loan-for-teachers" element={<Teachers />} />
            <Route path="Instant-personal-loan-for-two-wheeler" element={<TwoWheeler />} />
            <Route path="Instant-personal-loan-for-shopping" element={<Shopping />} />
            <Route path="Instant-personal-loan-for-engineer" element={<Engineer />} />
          </Route>

          {/* ─── Dashboard Layout ─── */}
          <Route path="/my-dashboard" element={<LoanLayout />}>
            <Route path="/my-dashboard/" element={<DashBoard showmessage={showmessage} />} />
            <Route path="/my-dashboard/application-flow" element={<CombinedApplicationFlow />} />
            <Route path="/my-dashboard/pan-details" element={<PanDetails />} />
            <Route path="/my-dashboard/captur-address" element={<CapturAddress />} />
            <Route path="/my-dashboard/captur-personal-information" element={<CapturPersonalInformation />} />
            <Route path="/my-dashboard/captur-income-details" element={<CapturIncomeDetails />} />
            <Route path="/my-dashboard/upload-picture" element={<UploadPicture />} />
            <Route path="/my-dashboard/user-profile" element={<ProfilePage />} />
            <Route path="/my-dashboard/profile-preview" element={<ProfilePreview />} />
            <Route path="/my-dashboard/calculate-loan" element={<CalculateLoan />} />
            <Route path="/my-dashboard/about-your-company" element={<AboutCompany />} />
            <Route path="/my-dashboard/bank-detail" element={<BankDetail />} />
            <Route path="/my-dashboard/kyc" element={<Ekyc />} />
            <Route path="/my-dashboard/bank-upload" element={<UploadBankStatement />} />
            <Route path="/my-dashboard/pan-upload" element={<PanUpload />} />
            <Route path="/my-dashboard/adhar-upload" element={<DocumentUpload />} />
            <Route path={EKYC_STEP_PATH} element={<AadhaarEkycStep />} />
            <Route path={AA_STEP_PATH} element={<AccountAggregatorStep />} />
            <Route path={THANK_YOU_PATH} element={<ThankYou />} />
            <Route path="/my-dashboard/congratulations" element={<LegacyEkycRedirect />} />
            <Route path="/my-dashboard/upload-salaryslip" element={<UploadSalaryslip />} />
            <Route path="/my-dashboard/upload-utilitybill" element={<UploadUtilitybill />} />
            <Route path="/my-dashboard/lead-preview" element={<LeadPreview />} />
            <Route path="/my-dashboard/leads" element={<LoanHistory />} />
            <Route path="/my-dashboard/repayment" element={<RepayPage />} />
            <Route path="/my-dashboard/details" element={<LoanDetail />} />
            <Route path="/my-dashboard/eligibility" element={<ProtectedRoute redirectTo="/my-dashboard" element={<Eligibility />} />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
}
export default Router;